import { ROUTES } from '@/constants/routes';
import {
  LINKED_ORGANIZATION_STORAGE_KEY,
  ORGANIZATION_ARRIVAL_STORAGE_KEY,
} from '@/constants/storage-keys';
import {
  ORGANIZATION_QUERY_PARAM,
  setTabOrganizationId,
} from '@/lib/current-organization';
import { enterIdentity, enterIdentityInPlace } from '@/lib/identity';
import { withoutSearchParam } from '@/lib/utils';

// The router parses `?org=12` as a number; a hand-typed or quoted one stays a string.
const organizationIdOf = (value: unknown): number | null => {
  const raw = typeof value === 'number' ? String(value) : value;
  return typeof raw === 'string' && /^[1-9]\d{0,9}$/.test(raw)
    ? Number(raw)
    : null;
};

const take = (key: string): number | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    window.sessionStorage.removeItem(key);
    return organizationIdOf(raw);
  } catch {
    return null;
  }
};

const store = (key: string, organizationId: number): void => {
  try {
    window.sessionStorage.setItem(key, String(organizationId));
  } catch {
    /* private mode — the link still opens, in the tab's current workspace */
  }
};

// Held before `/auth/me`, so a login, verification or failed refresh in between keeps it.
export const holdLinkedOrganization = (
  search: Record<string, unknown>,
): void => {
  const organizationId = organizationIdOf(search[ORGANIZATION_QUERY_PARAM]);
  if (organizationId !== null) {
    store(LINKED_ORGANIZATION_STORAGE_KEY, organizationId);
  }
};

export const takeLinkedOrganization = (): number | null =>
  take(LINKED_ORGANIZATION_STORAGE_KEY);

export const takeOrganizationArrival = (): number | null =>
  take(ORGANIZATION_ARRIVAL_STORAGE_KEY);

const pathOf = (url: URL): string => `${url.pathname}${url.search}${url.hash}`;

export const withoutOrganizationParam = (href: string): string =>
  withoutSearchParam(href, ORGANIZATION_QUERY_PARAM);

const withOrganizationParam = (
  href: string,
  organizationId: number,
): string => {
  const url = new URL(href, window.location.origin);
  url.searchParams.set(ORGANIZATION_QUERY_PARAM, String(organizationId));
  return pathOf(url);
};

// Pins at once: it runs before anything mounts, so no "Leave site?" prompt can cancel this load.
export const enterLinkedOrganization = (
  organizationId: number,
  href: string,
): void => {
  store(ORGANIZATION_ARRIVAL_STORAGE_KEY, organizationId);
  setTabOrganizationId(organizationId);
  enterIdentityInPlace(withoutOrganizationParam(href));
};

// A link like any other, so a cancelled load changes nothing; other tabs keep their state.
export const switchTabOrganization = (
  organizationId: number,
  // An entity URL from the outgoing organization would only be a not-found in the next one.
  destination: string = ROUTES.DASHBOARD,
): void => {
  enterIdentity(withOrganizationParam(destination, organizationId));
};
